// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { activityStrings } from './strings';
import type { ActivityKind } from './types';

export interface ActivityFiltersBarProps {
  readonly active: ActivityKind;
  readonly onChange: (kind: ActivityKind) => void;
}

const ALL_FILTERS: readonly ActivityKind[] = [
  'all',
  'dm',
  'mention',
  'reminder',
  'missedCall',
  'reaction',
  'task',
  'system',
];

/** Horizontally-scrolling filter chips. One chip per {@link ActivityKind}. */
export const ActivityFiltersBar = React.memo<ActivityFiltersBarProps>(({ active, onChange }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const handlePress = useCallback(
    (kind: ActivityKind) => {
      if (kind !== active) {
        onChange(kind);
      }
    },
    [active, onChange],
  );

  return (
    <View
      style={[
        styles.bar,
        {
          backgroundColor: theme.colors.surface.primary,
          borderBottomColor: theme.colors.border.secondary,
        },
      ]}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {ALL_FILTERS.map((kind) => {
          const selected = kind === active;
          return (
            <Pressable
              key={kind}
              onPress={() => handlePress(kind)}
              accessibilityRole="button"
              accessibilityLabel={activityStrings.filter(kind)}
              accessibilityState={{ selected }}
              style={({ pressed }) => [
                styles.chip,
                {
                  borderColor: selected
                    ? theme.colors.accent.indigo
                    : theme.colors.border.secondary,
                  backgroundColor: selected ? `${theme.colors.accent.indigo}1A` : 'transparent',
                  opacity: pressed ? 0.7 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.chipText,
                  {
                    color: selected ? theme.colors.accent.indigo : theme.colors.text.primary,
                  },
                ]}
              >
                {activityStrings.filter(kind)}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
});

ActivityFiltersBar.displayName = 'ActivityFiltersBar';

/** Convenience export so tests can iterate over the same source of truth. */
export const ACTIVITY_FILTERS = ALL_FILTERS;

const styles = StyleSheet.create({
  bar: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  scrollContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexDirection: 'row',
  } satisfies ViewStyle,
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
    marginRight: 8,
  } satisfies ViewStyle,
  chipText: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
});
